export type CutDayIndicatorDirection = "left" | "right";

export const CUT_DAY_NAVIGATE_EVENT = "cut-day-navigate";
export type CutDayNavigateEventDetail = { direction: CutDayIndicatorDirection };

export type TimelineWeekCutDayIndicatorsState = {
	leftCutDayDate: string | null;
	rightCutDayDate: string | null;
};

/**
 * Custom element that renders left/right cut-day indicators for the week timeline.
 * Contains a `leftIndicator` and `rightIndicator` div that show which days are clipped by the scroll
 * viewport. `onIndicatorClick` and `onIndicatorKeydown` are delegated input handlers that fire
 * a navigation request so the caller can scroll the timeline to the hidden day.
 *
 * The component is intentionally stateless regarding viewport geometry:
 * callers pass precomputed visibility dates via `render(...)`.
 */
export class TimelineWeekCutDayIndicators extends HTMLElement {
	private enabled = false;
	private leftIndicator: HTMLButtonElement | null = null;
	private rightIndicator: HTMLButtonElement | null = null;
	private readonly onIndicatorClick = (event: MouseEvent): void => this.handleIndicatorClick(event);

	/**
	 * Initializes static indicator markup and delegated input handlers.
	 * Event delegation keeps the listener count constant even when indicator content changes.
	 */
	public connectedCallback(): void {
		this.className = "timeline-cut-day-indicators";
		this.innerHTML = `
				<button class="timeline-cut-day-indicator timeline-cut-day-indicator-left" data-cut-day-indicator-left data-cut-day-indicator-direction="left" hidden></button>
				<button class="timeline-cut-day-indicator timeline-cut-day-indicator-right" data-cut-day-indicator-right data-cut-day-indicator-direction="right" hidden></button>
		`;
		this.leftIndicator = this.querySelector("button[data-cut-day-indicator-left]");
		this.rightIndicator = this.querySelector("button[data-cut-day-indicator-right]");
		this.addEventListener("click", this.onIndicatorClick);
	}

	/**
	 * Releases DOM references and handlers to avoid stale callbacks after detaching.
	 */
	public disconnectedCallback(): void {
		this.removeEventListener("click", this.onIndicatorClick);
		this.leftIndicator = null;
		this.rightIndicator = null;
	}

	/**
	 * Globally enables/disables indicator rendering.
	 * Disabled state always forces both indicators hidden.
	 */
	public setEnabled(enabled: boolean): void {
		this.enabled = enabled;
		if (!enabled) {
			this.hideAll();
		}
	}

	/**
	 * Renders indicator visibility/content from precomputed cut-day dates.
	 */
	public render(state: TimelineWeekCutDayIndicatorsState): void {
		const leftIndicator = this.leftIndicator;
		const rightIndicator = this.rightIndicator;

		if (!leftIndicator || !rightIndicator || !this.enabled) {
			this.hide(leftIndicator, rightIndicator);
			return;
		}

		this.renderCutDayIndicator(leftIndicator, state.leftCutDayDate, "left");
		this.renderCutDayIndicator(rightIndicator, state.rightCutDayDate, "right");
	}

	private handleIndicatorClick(event: MouseEvent): void {
		const indicator = this.getIndicatorFromEvent(event);
		if (!indicator) {
			return;
		}
		this.emitNavigationRequest(this.getIndicatorDirection(indicator));
	}

	/**
	 * Resolves an indicator node from a bubbled event target.
	 */
	private getIndicatorFromEvent(event: Event): HTMLElement | null {
		const target = event.target;
		if (!(target instanceof HTMLElement)) {
			return null;
		}
		return target.closest("[data-cut-day-indicator-direction]");
	}

	private getIndicatorDirection(indicator: HTMLElement): CutDayIndicatorDirection {
		return indicator.dataset.cutDayIndicatorDirection as CutDayIndicatorDirection;
	}

	private emitNavigationRequest(direction: CutDayIndicatorDirection): void {
		this.dispatchEvent(new CustomEvent<CutDayNavigateEventDetail>(CUT_DAY_NAVIGATE_EVENT, {detail: {direction}, bubbles: true}));
	}

	/**
	 * Hides both indicators.
	 */
	private hideAll(): void {
		this.hide(this.leftIndicator, this.rightIndicator);
	}

	/**
	 * Utility to hide provided indicator nodes when available.
	 */
	private hide(leftIndicator: HTMLButtonElement | null, rightIndicator: HTMLButtonElement | null): void {
		if (leftIndicator) {
			leftIndicator.hidden = true;
		}
		if (rightIndicator) {
			rightIndicator.hidden = true;
		}
	}

	/**
	 * Updates a single indicator:
	 * - hides when no date is available or date is invalid
	 * - renders localized weekday text with directional caret when valid
	 */
	private renderCutDayIndicator(indicator: HTMLButtonElement, dateString: string | null, direction: CutDayIndicatorDirection): void {
		if (!dateString) {
			indicator.hidden = true;
			return;
		}

		const date = moment.parseZone(dateString);
		if (!date.isValid()) {
			indicator.hidden = true;
			return;
		}

		// Use localized weekday name as content and prepend/append a directional caret to indicate scroll direction.
		const dayName = date.format("dddd");
		indicator.innerHTML = direction === "left"
			? `<i class="fas fa-caret-left position-left"></i>${dayName}`
			: `${dayName}<i class="fas fa-caret-right ml-5"></i>`;
		indicator.hidden = false;
	}
}
